import { useEffect, useState, useRef, ReactNode, useCallback } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";
import { EventListener } from "react-lottie";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Spinner from "@atlaskit/spinner";

import { useSearchParameters } from "@/components/signup/hooks";
import {
    JiraFFLOverlayTemplate,
    JiraOverlayTemplate
} from "@/components/signup/layouts";
import {
    ufoEventRedirectToProduct,
    UfoCustomTimingKey,
    UfoCustomTimingWrapper
} from "@/components/signup/utils";
import { DwellTimeWrapper } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/components/dwell-time-wrapper";
import {
    useCloudStatus,
    useCloudStatusRedirection
} from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks";
import { useCreateJiraFflProject } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-create-jira-ffl-project";
import { trackPerceivedProvisioningEvent } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/utils/analytics";
import { useCreateRedirectUrlWithWizard } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/utils/create-redirect-url";
import { experiments, getExperiment } from "@/utils/experiment";
import { FeatureExposed } from "@/utils/feature-exposed";

import { LottieAnimationContainer } from "../../../../components";

export const ControlAnimation = ({
    redirectOnAnimationComplete = true
}: {
    redirectOnAnimationComplete?: boolean;
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loop, setLoop] = useState(false);
    const [topOrTail, setTopOrTail] = useState("top");
    const [override, setOverride] = useState(false);
    const redirecting = useRef(false);
    const [loopAnimation, setLoopAnimation] = useState(null);
    const [tailAnimations, setTailAnimations] = useState([]);
    const [topAnimations, setTopAnimations] = useState([]);

    const provisioningComplete = useRef(false);

    const { createAnalyticsEvent } = useAnalyticsEvents();

    const { createCloudStatus, perceivedProvisioningStartTime, cloudId } =
        useCloudStatus();

    const {
        redirectUrl,
        deferRedirectView,
        setDeferRedirectView,
        resolveBlockingRedirectPromises
    } = useCloudStatusRedirection();

    const { products, signupId, jiraFflCgsKey, taskIdParam } =
        useSearchParameters();
    const productKey = products.length > 0 ? products[0] : undefined;

    const createRedirectUrlWithWizard = useCreateRedirectUrlWithWizard();

    const jiraFflExperiment = getExperiment(experiments.cfind4666JiraFfl);
    const isJiraFflExperimentVariant =
        jiraFflExperiment.type === "enrolled" &&
        jiraFflExperiment.value === true;

    // Only force the flow when the user is explicitly bucketed into the variant.
    // If we fall back to default, we treat it as "off".
    const shouldForceProvisioningOnlyForJiraFfl =
        isJiraFflExperimentVariant && jiraFflCgsKey;

    const { hasCreatedProject, createJiraProject } = useCreateJiraFflProject({
        enabled: Boolean(shouldForceProvisioningOnlyForJiraFfl),
        cgsKey: jiraFflCgsKey
    });

    // Trigger Jira project creation when provisioning completes (FFL variant only).
    useEffect(() => {
        if (
            shouldForceProvisioningOnlyForJiraFfl &&
            createCloudStatus === "success" &&
            cloudId &&
            !hasCreatedProject
        ) {
            createJiraProject();
        }
    }, [
        shouldForceProvisioningOnlyForJiraFfl,
        createCloudStatus,
        cloudId,
        hasCreatedProject,
        createJiraProject
    ]);

    const redirectCallback = useCallback(async () => {
        createAnalyticsEvent({
            eventType: "ui",
            action: "succeeded",
            actionSubject: "provisioning",
            actionSubjectId: "jiraAnimatedProvisioningScreen",
            source: "/ondemand/signup/confirmation",
            attributes: {
                atlasId: "ATLAS-56326"
            }
        }).fire();

        redirecting.current = true;

        createAnalyticsEvent({
            eventName: "redirectedToProduct",
            eventComponent: "confirmationPage",
            event: "redirected",
            eventType: "ui",
            action: "redirected"
        }).fire();

        trackPerceivedProvisioningEvent({
            createAnalyticsEvent,
            productKey,
            finalProvisioningScreen: "jira-animated-provisioning-screen",
            startTimeSeconds: perceivedProvisioningStartTime,
            signupId
        });

        // SLO "good event" for redirect into product
        ufoEventRedirectToProduct.success({
            customData: {
                reactComponent: "JiraAnimatedProvisioningScreen",
                analyticsEvent: "redirected",
                redirectFrom: "confirmationPage",
                finalProvisioningScreen: "jira-animated-provisioning-screen"
            }
        });

        await resolveBlockingRedirectPromises();

        const finalRedirectUrl = taskIdParam
            ? createRedirectUrlWithWizard(redirectUrl, taskIdParam)
            : redirectUrl;

        // Timeout is needed to ensure that analytics events are able to be sent before browser redirects, otherwise it will abort them.
        setTimeout(() => {
            window.location.assign(finalRedirectUrl);
        }, 100);
    }, [
        createAnalyticsEvent,
        perceivedProvisioningStartTime,
        productKey,
        redirectUrl,
        resolveBlockingRedirectPromises,
        signupId,
        createRedirectUrlWithWizard,
        taskIdParam
    ]);

    const eventListener: EventListener[] = [
        {
            eventName: "loopComplete",
            callback: () => {
                const lastAnimation = tailAnimations.length - 1;
                if (provisioningComplete.current) {
                    if (loop) {
                        setLoop(false);
                        setCurrentIndex(lastAnimation);
                        setTopOrTail("tail");
                    }
                }
            }
        },
        {
            eventName: "complete",
            callback: () => {
                const lastAnimation = tailAnimations.length - 1;
                if (provisioningComplete.current) {
                    // Stop playing animation when redirecting
                    if (redirecting.current) {
                        return;
                    }

                    // Redirect after final tail animation completes
                    if (topOrTail === "tail") {
                        if (redirectOnAnimationComplete) {
                            redirectCallback();
                        }
                    } else {
                        setTopOrTail("tail");
                    }
                } else if (currentIndex < lastAnimation) {
                    setCurrentIndex(currentIndex + 1);
                } else if (currentIndex === lastAnimation) {
                    //loop on finishing up
                    setLoop(true);

                    createAnalyticsEvent({
                        eventType: "ui",
                        action: "started",
                        actionSubject: "animation",
                        actionSubjectId:
                            "jiraAnimatedProvisioningScreenLoopAnimation",
                        source: "/ondemand/signup/confirmation",
                        attributes: {
                            atlasId: "ATLAS-56326"
                        }
                    }).fire();
                }
            }
        }
    ];

    const getAnimation = () => {
        if (loop) {
            return loopAnimation;
        } else if (topOrTail === "top") {
            return topAnimations[currentIndex];
        } else {
            return tailAnimations[currentIndex];
        }
    };

    const boldSpan = (child: ReactNode) => <span css={boldStyle}>{child}</span>;

    const text = [
        <div key="0">
            <FormattedMessage
                id="jira.provisioning.preparing-workspace"
                defaultMessage={`Preparing your <tag>workspace</tag>`}
                values={{
                    tag: boldSpan
                }}
            />
        </div>,
        <div key="1">
            <FormattedMessage
                id="jira.provisioning.building-timeline"
                defaultMessage={`Building your <tag>timeline</tag>...`}
                values={{
                    tag: boldSpan
                }}
            />
        </div>,
        <div key="2">
            <FormattedMessage
                id="jira.provisioning.creating-board"
                defaultMessage={`Creating your <tag>board</tag>...`}
                values={{
                    tag: boldSpan
                }}
            />
        </div>,
        <div key="3">
            <FormattedMessage
                id="jira.provisioning.setting-reports"
                defaultMessage={`Setting up your <tag>reports</tag>...`}
                values={{
                    tag: boldSpan
                }}
            />
        </div>,
        <div key="4">
            <FormattedMessage
                id="jira.provisioning.finishing-up"
                defaultMessage={`Finishing up ...`}
            />
        </div>
    ];

    const animationsLoaded = loopAnimation && tailAnimations && topAnimations;

    useEffect(() => {
        const runSetAnimationMap = async () => {
            let animations;
            try {
                animations = await Promise.all([
                    import("../lotties/1TAIL.json") as Promise<never>,
                    import("../lotties/1TOP.json") as Promise<never>,
                    import("../lotties/2TAIL.json") as Promise<never>,
                    import("../lotties/2TOP.json") as Promise<never>,
                    import("../lotties/3TAIL.json") as Promise<never>,
                    import("../lotties/3TOP.json") as Promise<never>,
                    import("../lotties/4TAIL.json") as Promise<never>,
                    import("../lotties/4TOP.json") as Promise<never>,
                    import("../lotties/5REFLOOP.json") as Promise<never>,
                    import("../lotties/6LOOP.json") as Promise<never>,
                    import("../lotties/7OUTRO.json") as Promise<never>
                ]);
            } catch (e) {
                console.error(e);
            }

            if (animations) {
                const [
                    tail01,
                    top01,
                    tail02,
                    top02,
                    tail03,
                    top03,
                    tail04,
                    top04,
                    top05,
                    loopAnimation,
                    tail05
                ] = animations;
                const topAnimations = [top01, top02, top03, top04, top05];
                const tailAnimations = [tail01, tail02, tail03, tail04, tail05];
                setTopAnimations(topAnimations);
                setTailAnimations(tailAnimations);
                setLoopAnimation(loopAnimation);
            }
        };
        runSetAnimationMap();
    }, []);

    useEffect(() => {
        if (!redirectOnAnimationComplete) {
            return;
        }

        // For Jira FFL variant, wait for project creation to complete before allowing the
        // animation tail + redirect flow to proceed (matches TWC createJiraProject gating).
        if (shouldForceProvisioningOnlyForJiraFfl && !hasCreatedProject) {
            return;
        }

        if (createCloudStatus === "success" || override) {
            provisioningComplete.current = true;

            // if tab inactive react-lottie 'complete' event doesn't fire
            // Redirect if tab is inactive
            if (document.hidden) {
                redirectCallback();
            }

            setLoop(false);
            setOverride(false);
        }
    }, [
        redirectOnAnimationComplete,
        createCloudStatus,
        override,
        shouldForceProvisioningOnlyForJiraFfl,
        hasCreatedProject,
        redirectCallback,
        createAnalyticsEvent,
        perceivedProvisioningStartTime,
        productKey,
        createRedirectUrlWithWizard,
        taskIdParam
    ]);

    // Prevent use-cloud-status.tsx from immediately redirecting when provisioning is finished.
    useEffect(() => {
        if (!deferRedirectView) {
            if (setDeferRedirectView) {
                setDeferRedirectView("jira-animated-provisioning-screen");
            }
        }
    });

    // Initial analytics events
    useEffect(() => {
        createAnalyticsEvent({
            eventType: "screen",
            name: "jiraAnimatedProvisioningScreen",
            attributes: {
                atlasId: "ATLAS-56326"
            }
        }).fire();
    }, [createAnalyticsEvent]);

    const rotatingMessages = (
        <div css={textStyle}>
            {topOrTail === "tail" ? (
                <FormattedMessage
                    id="jira.provisioning.all-set"
                    defaultMessage={`You're all set`}
                />
            ) : (
                text[currentIndex]
            )}
        </div>
    );

    const animations = (
        <>
            {animationsLoaded && (
                <LottieAnimationContainer
                    options={{
                        loop: loop,
                        autoplay: true,
                        animationData: getAnimation(),
                        rendererSettings: {
                            preserveAspectRatio: "xMidYMid slice"
                        }
                    }}
                    eventListener={eventListener}
                />
            )}
            {!animationsLoaded && (
                <div css={spinnerContainer}>
                    <Spinner size="large" />
                </div>
            )}
        </>
    );

    return (
        <UfoCustomTimingWrapper
            ufoEvent={ufoEventRedirectToProduct}
            ufoCustomTimingKey={
                UfoCustomTimingKey.JIRA_ANIMATED_PROVISIONING_DWELL_TIME
            }
        >
            <DwellTimeWrapper
                eventData={{
                    action: "dwelled",
                    actionSubject: "screen",
                    actionSubjectId: "jiraAnimatedProvisioningScreen",
                    source: "/ondemand/signup/confirmation",
                    attributes: {
                        atlasId: "ATLAS-56326"
                    }
                }}
                initialPollInterval={5000}
                idleTimeout={120000}
                nextPollIntervalCallback={(x) => x + 5000}
            >
                {shouldForceProvisioningOnlyForJiraFfl ? (
                    <JiraFFLOverlayTemplate size="sm">
                        {rotatingMessages}
                        <div css={overlayAnimationContainer}>{animations}</div>
                    </JiraFFLOverlayTemplate>
                ) : (
                    <FeatureExposed
                        experimentKey="jfind-1565-sign-up-flow-overlay"
                        group="Test"
                    >
                        <JiraOverlayTemplate size="lg">
                            {rotatingMessages}
                            <div css={overlayAnimationContainer}>
                                {animations}
                            </div>
                        </JiraOverlayTemplate>
                    </FeatureExposed>
                )}
            </DwellTimeWrapper>
        </UfoCustomTimingWrapper>
    );
};
const textStyle = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "var(--color-text-subtle, #44546F)",
    textAlign: "center",
    fontSize: "16px",
    marginBlockEnd: "17px"
});

const boldStyle = css({
    fontWeight: "600"
});

const spinnerContainer = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "337px"
});

const overlayAnimationContainer = css({
    marginInline: "auto",
    maxWidth: 487
});
